/* eslint-disable @typescript-eslint/no-explicit-any */
import { CloseOutlined } from '@ant-design/icons';
import { Button, Col, Input, InputRef, Popover, Row } from 'antd';
import { PopoverProps } from 'antd/lib/popover';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { AnchorContext } from 'components/atoms/AnchorComponent';
import { globalConfig } from 'components/hoc/Configuration';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';

import { editaceTextuPost } from 'services/editaceTextu/editaceTextu';

import { ENotificationType } from 'enums/notificationType';

import getLanguageObject from 'utils/getLanguageObject';

import useProfily from '../../hooks/useProfily';

interface Ip extends PopoverProps {
    text: string;
    displayPopover: boolean;
    setDisplayPopover: React.Dispatch<React.SetStateAction<boolean>>;
    onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    setSelectOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const LanguagePopoverWrapper: React.FC<Ip> = props => {
    const { text, displayPopover, setDisplayPopover, onClick, setSelectOpen, children } = props;
    const { link, to, href, anchorOnClick } = useContext(AnchorContext);
    const { language, editaceTextu, serviceMode, NotificationComponent } = useContext(Context);
    const { setLevel } = useProfily();
    const textKey = text;
    const [changeValueCZ, setChangeValueCZ] = useState<string>();
    const [changeValueEN, setChangeValueEN] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const displayService = globalConfig.config.serviceText && serviceMode;
    const inputCZref = useRef<InputRef>(null);
    const inputENref = useRef<InputRef>(null);

    useEffect(() => {
        // automaticke zatvaranie okna pri kliku mimo
        const root = document.getElementById('root');
        const select = Array.from(document.getElementsByClassName('ant-select-dropdown')) || [];

        setChangeValueCZ(editaceTextu?.cz[textKey] || '');
        setChangeValueEN(editaceTextu?.en[textKey] || '');

        const eventFunc = () => {
            displayPopover && setDisplayPopover(false);
        };
        if (displayPopover) {
            setTimeout(() => {
                root?.addEventListener('click', eventFunc);
                select.length && select.map(e => e.addEventListener('click', eventFunc));
            }, 0);
        }
        return () => {
            root?.removeEventListener('click', eventFunc);
            select.length && select.map(e => e.removeEventListener('click', eventFunc));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayPopover]);

    const submitChange = () => {
        setLoading(true);
        const dataToSend = {
            id: editaceTextu?.id!,
            cz: {
                ...editaceTextu?.cz,
                [textKey]: changeValueCZ
            },
            en: {
                ...editaceTextu?.en,
                [textKey]: changeValueEN
            }
        };
        (getLanguageObject('cz')[textKey] === changeValueCZ || changeValueCZ === '') && delete dataToSend.cz[textKey];
        (getLanguageObject('en')[textKey] === changeValueEN || changeValueEN === '') && delete dataToSend.en[textKey];
        editaceTextuPost({
            ...dataToSend,
            cz: JSON.stringify(dataToSend.cz),
            en: JSON.stringify(dataToSend.en)
        })
            .then(data => {
                setLevel('editaceTextu', {
                    id: data.id,
                    cz: JSON.parse(data.cz),
                    en: JSON.parse(data.en)
                });
                setLoading(false);
                setChangeValueCZ('');
                setChangeValueEN('');
                setDisplayPopover(false);
            })
            .catch(err => {
                if (err.response.status === 409) {
                    const czParsed = JSON.parse(err.response.data.cz);
                    const enParsed = JSON.parse(err.response.data.en);

                    if (
                        (Object.keys(czParsed).includes(textKey) && czParsed[textKey] !== changeValueCZ) ||
                        (Object.keys(enParsed).includes(textKey) && enParsed[textKey] !== changeValueEN)
                    ) {
                        setLevel('editaceTextu', {
                            id: err.response.data.id,
                            cz: czParsed,
                            en: enParsed
                        });
                        NotificationComponent({
                            title: 'Změna překladu',
                            description:
                                // eslint-disable-next-line max-len
                                'Je k dispozici novější soubor s překladem, soubor byl načten a aplikován, provedte kontrolu překladu a aplikujte změnu znovu.',
                            type: ENotificationType.WARNING
                        });
                        setLoading(true);
                    } else {
                        const mergedData = {
                            id: err.response.data.id,
                            cz: {
                                ...editaceTextu?.cz,
                                ...czParsed,
                                [textKey]: changeValueCZ
                            },
                            en: {
                                ...editaceTextu?.en,
                                ...enParsed,
                                [textKey]: changeValueEN
                            }
                        };
                        setLevel('editaceTextu', mergedData);
                        setTimeout(() => submitChange());
                    }
                } else {
                    setLoading(false);
                }
            });
    };

    return (
        <Popover
            className="languagePopover"
            overlayInnerStyle={{
                padding: 0
            }}
            content={
                <LoadingWrapper loading={loading}>
                    <Col onClick={e => e.stopPropagation()}>
                        <Col
                            style={{
                                padding: 0,
                                width: 300
                            }}
                        >
                            {getLanguageObject(language)[textKey]}
                            <Row>
                                <b
                                    style={{
                                        marginTop: 3
                                    }}
                                >
                                    CZ
                                </b>
                                <Input
                                    ref={inputCZref}
                                    onBlur={e => e.stopPropagation()}
                                    onClick={() => {
                                        inputCZref?.current?.focus();
                                    }}
                                    value={changeValueCZ}
                                    style={{
                                        width: '86%',
                                        marginLeft: 10,
                                        marginBottom: 4
                                    }}
                                    onChange={e => {
                                        setChangeValueCZ(e.target.value);
                                    }}
                                    onPressEnter={submitChange}
                                />
                            </Row>
                            <Row>
                                <b
                                    style={{
                                        marginTop: 3
                                    }}
                                >
                                    EN
                                </b>
                                <Input
                                    onBlur={e => e.stopPropagation()}
                                    ref={inputENref}
                                    onClick={() => {
                                        inputENref?.current?.focus();
                                    }}
                                    value={changeValueEN}
                                    style={{
                                        width: '86%',
                                        marginLeft: 10
                                    }}
                                    onChange={e => {
                                        setChangeValueEN(e.target.value);
                                    }}
                                    onPressEnter={submitChange}
                                />
                            </Row>
                        </Col>
                        <Row
                            align="middle"
                            justify="space-around"
                            style={{
                                position: 'relative',
                                left: -10
                            }}
                        >
                            <Button type="link" onClick={submitChange}>
                                Změnit text
                            </Button>
                            {link ? (
                                <Link to={to || ''} onClick={onClick || anchorOnClick}>
                                    Primární funkce
                                </Link>
                            ) : href || anchorOnClick || setSelectOpen ? (
                                <a
                                    href={href}
                                    onClick={
                                        setSelectOpen
                                            ? () => {
                                                  setDisplayPopover(false);
                                                  setSelectOpen && setSelectOpen(false);
                                              }
                                            : onClick || anchorOnClick
                                    }
                                >
                                    Primární funkce
                                </a>
                            ) : (
                                onClick && (
                                    <Button type="link" onClick={onClick}>
                                        Primární funkce
                                    </Button>
                                )
                            )}
                        </Row>
                    </Col>
                </LoadingWrapper>
            }
            style={{
                zIndex: 9999
            }}
            open={displayService && displayPopover}
            title={
                <Row
                    justify="space-between"
                    style={{
                        height: 22
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <p>{textKey}</p>
                    <CloseOutlined
                        onClick={() => {
                            setDisplayPopover(false);
                            setSelectOpen && setSelectOpen(false);
                        }}
                    />
                </Row>
            }
        >
            {children}
        </Popover>
    );
};
export default LanguagePopoverWrapper;
